import { useState, useEffect, useMemo } from 'react';
import {
  obtenerArchivosLista,
  ObtenerArchivo,
  DescargarArchivos,
} from '../../redux/solicitudesAdmSlice';

import { useDispatch, useSelector } from 'react-redux';
import OperationStatus from '../../config/OperationStatus';
import FileViewer from '../../Components/FileViewer';
import { toast } from 'react-toastify';
import { OrderTable } from '../../Components/OrderTable';
import { useForm } from 'react-hook-form';
import Modal from './Modal';
import WindowDimension from '../../Hooks/WindowDimension';
import Required from '../../Components/Required';
import LoadingSpiner from '../../Components/LoadingSpiner';
import Pagination from './Paginacion';
import { debounce } from 'radash';
import { getAdminFilePage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { useValidation } from "../../Hooks/useValidation";
import { Skeleton } from "antd";
import { _translateValue } from "../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaListaArchivos";

function ListaArchivos(props) {
  const dispatch = useDispatch();
  const { width } = WindowDimension();

  const [tableData, setTableData] = useState([]);
  const [docs, setDocs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDownloadModal, setDownloadModal] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [rowCount, setRowCount] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("");
  const [search, setSearch] = useState("");
  const { locale } = useLocale();
  const { validation } = useValidation();
  const [adminFilePage, setAdminFilePage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedArchivos, setTranslatedArchivos] = useState({});
  const [translatedResponsables, setTranslatedResponsables] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);

  const columns = useMemo(() => {
    const fileTable = adminFilePage?.file_table ?? [];
    return [
      { label: fileTable[0]?.label || 'Código', accessor: 'archivoId', sortable: true },
      { 
        label: fileTable[1]?.label || 'Archivo', 
        accessor: 'archivoNombre', 
        sortable: true,
        render: (item) => (
          <td>{translatedArchivos[item.archivoNombre] || item.archivoNombre}</td>
        ),
      },
      { 
        label: fileTable[2]?.label || 'Responsable', 
        accessor: 'nombreCompleto', 
        sortable: true,
        render: (item) => (
          <td>{translatedResponsables[item.nombreCompleto] || item.nombreCompleto}</td>
        ),
      },
      { label: fileTable[3]?.label || 'Fecha', accessor: 'fecha', sortable: true },
      { label: fileTable[4]?.label || 'Acción', accessor: 'action', sortable: false },
    ];
  }, [adminFilePage, translatedArchivos, translatedResponsables]);

  const { archivosListado, loadingArchivos } = useSelector((state) => state.solicitudesAdm);

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm();

  const obtenerArchivo = async (archivoId) => {
    setDocs([
      {
        uri: process.env.REACT_APP_BASEURL + 'admin/archivos/File/' + archivoId,
      },
    ]);
    setShowModal(true);
  };

  const descargarArchivo = async (archivoId, name) => {
    dispatch(ObtenerArchivo({ id: archivoId, name: name })).then(
      ({ payload }) => {
        if (payload.operationStatus === OperationStatus.SUCCESS) {
          toast.success(payload.message);
        } else {
          toast.error(payload.message);
        }
      }
    );
  };

  const onSubmit = async (data) => {
    setLoadingDetalle(true);
    let { payload } = await dispatch(DescargarArchivos(data.id));
    setLoadingDetalle(false);
    if (payload.operationStatus === OperationStatus.SUCCESS) {
      toast.success(payload.message);
      setDownloadModal(false);
      reset();
    } else {
      toast.error(payload.message);
    }
  };

  useEffect(() => {
    const fn = async () => {
      const result = await dispatch(obtenerArchivosLista({ rowCount, page: currentPage , sortColumn , search }));
      // setTableData(result.payload.result);
    }

    fn();
  }, [currentPage, rowCount , sortColumn , search ]);

  const handleTextChange = debounce({ delay : 500 }, (e) => {
    let val = e.target.value.toLowerCase();

   setSearch(val);
    
  });

  const closeModalEdit = () => {
    setShowModal(false);
  };

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminFilePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminFilePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminFilePage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir los nombres de archivos y responsables cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasArchivos = archivosListado && Array.isArray(archivosListado) && archivosListado.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasArchivos) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const archivoTranslations = {};
      const responsableTranslations = {};

      // Traducir archivos y responsables
      const translationPromises = archivosListado.map(async (archivo) => {
        if (archivo.archivoNombre) {
          const translated = await _translateValue(SCREEN_NAME, archivo.archivoNombre, locale);
          archivoTranslations[archivo.archivoNombre] = translated;
        }
        if (archivo.nombreCompleto) {
          const translated = await _translateValue(SCREEN_NAME, archivo.nombreCompleto, locale);
          responsableTranslations[archivo.nombreCompleto] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedArchivos(archivoTranslations);
      setTranslatedResponsables(responsableTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [archivosListado, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <main className="main-admin">
        <div className="main-admin-bar">
          <Skeleton.Input style={{ width: 300, height: 30 }} active />
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
          </div>
        </div>
        <div className="table-container relative-class">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <Skeleton.Input style={{ width: 300, height: 40 }} active />
        </div>
      </main>
    );
  }

  return (
    <main className="main-admin">
      <div className="main-admin-bar">
        <h2>{adminFilePage?.title || "Listado de archivos"}</h2>

        <div className="header-admin-bar-items ml-auto">
          <label hidden>{adminFilePage?.search_input || "Búsqueda"}</label>
          <input
            type="search"
            placeholder={adminFilePage?.search_input || "Búsqueda"}
            onInput={handleTextChange}
            className="form-control"
          />
        </div>
        <button
          className="search-button"
          onClick={() => setDownloadModal(true)}
        >
          <i className="material-icons"></i>
        </button>
      </div>

      <div className="table-container relative-class">
        {loadingArchivos && (
          <div className="loading-background">
            <LoadingSpiner
              show
              styles={{
                top: '45%',
                left: '50%',
                width: '50px',
                height: '50px',
                position: 'absolute',
                borderWidth: '10px',
                borderStyle: 'solid',
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderColor: 'rgb(27 78 147) rgb(0 0 0) rgb(0 0 0)',
                borderImage: 'initial',
              }}
            />
          </div>
        )}
        {archivosListado?.length > 0 ? (
          <>
            <OrderTable
              {...{ columns }}
              data={archivosListado ?? []}
              clase="table-adm"
              onSort={setSortColumn}
              sortColumn= {sortColumn}
              actions={[
                {
                  icon: '',
                  clase: 'text-primary',
                  event: (item) => obtenerArchivo(item.archivoId),
                },
                {
                  icon: '',
                  clase: 'text-primary',
                  event: (item) =>
                    descargarArchivo(item.archivoId, item.archivoNombre),
                },
              ]}
            />
          </>
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>
      <Pagination
        rowCount={rowCount}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onRowCountChange={setRowCount}
        totalPages={!loadingArchivos ? archivosListado[0]?.cantidadPaginas ?? 1 : 1}
      />
      <FileViewer
        estado={showModal}
        cambiarEstado={closeModalEdit}
        docs={docs}
      />
      <Modal
        estado={showDownloadModal}
        cambiarEstado={() => {
          setDownloadModal(false);
          reset();
        }}
        titulo={adminFilePage?.subt_download || 'Descargar archivos'}
        clase={`${width < 576 ? 'full-width' : 'small'}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminFilePage?.download_button || 'Descargar'}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <label>Id</label>
            <Required />
            <input
              className="form-control"
              type="text"
              {...register('id', {
                required: validation?.required_field || 'Campo requerido',
              })}
            />
            {errors.id && <p className="error">{errors.id.message}</p>}
          </div>
        </form>
      </Modal>
      <LoadingSpiner
        message={loadingMessage}
        show={loadingDetalle}
        fullPage={true}
        styles={{
          top: '45%',
          left: '50%',
          position: 'inherit',
          width: '50px',
          height: '50px',
          borderWidth: '10px',
          borderStyle: 'solid',
          borderColor: 'rgb(27 78 147) rgb(0 0 0) rgb(0 0 0)',
          borderImage: 'initial',
        }}
      />
    </main>
  );
}

export default ListaArchivos;
